import { Link } from "@inertiajs/react";
import { Box, Text, Link as CLink } from "@chakra-ui/react";
import { Player } from "@/types";

interface Props {
  player: Player;
  rank: number;
}

type TopRank = 1 | 2 | 3;

interface MedalStyle {
  bg: string;
  text: string;
  medal: string;
}

const medalStyles: Record<TopRank, MedalStyle> = {
  1: { bg: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)", text: "yellow.900", medal: "🥇" },
  2: { bg: "linear-gradient(135deg, #E8E8E8 0%, #B8B8B8 100%)", text: "gray.700", medal: "🥈" },
  3: { bg: "linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)", text: "orange.900", medal: "🥉" },
};

function isTopRank(rank: number): rank is TopRank {
  return rank === 1 || rank === 2 || rank === 3;
}

function getMedalStyle(rank: number): MedalStyle | null {
  return isTopRank(rank) ? medalStyles[rank] : null;
}

export function RankingCard({ player, rank }: Props) {
  const medalStyle = getMedalStyle(rank);
  const isTop3 = medalStyle !== null;

  return (
    <CLink asChild _hover={{ textDecoration: "none" }}>
      <Link href={`/@${player.player_id}`}>
        <Box
          bg="white"
          rounded="xl"
          shadow={isTop3 ? "lg" : "sm"}
          p={4}
          w="full"
          display="flex"
          alignItems="center"
          gap={4}
          transition="all 0.2s"
          _hover={{
            transform: "translateY(-2px)",
            shadow: "xl",
            borderColor: "blue.300"
          }}
          border="1px solid"
          borderColor={isTop3 ? "transparent" : "gray.100"}
          position="relative"
          overflow="hidden"
        >
          {/* 順位 */}
          <Box
            w="50px"
            h="50px"
            rounded="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontWeight="bold"
            fontSize={medalStyle ? "xl" : "lg"}
            flexShrink={0}
            bg={medalStyle?.bg ?? "gray.100"}
            color={medalStyle?.text ?? "gray.600"}
            shadow={medalStyle ? "md" : "none"}
            aria-label={`${rank}位`}
          >
            {medalStyle?.medal ?? rank}
          </Box>

          {/* プレイヤー情報 */}
          <Box flex={1} minW={0}>
            <Text
              fontWeight="bold"
              fontSize="md"
              truncate
              color="gray.800"
            >
              {player.display_name}
            </Text>
            <Text fontSize="xs" color="gray.500">
              @{player.player_id}
            </Text>
          </Box>

          {/* レーティング */}
          <Box textAlign="right" flexShrink={0}>
            <Text
              fontSize="2xl"
              fontWeight="bold"
              color={isTop3 ? "blue.600" : "gray.700"}
              lineHeight={1}
            >
              {player.latest_rating_score}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Rating
            </Text>
          </Box>

          {/* Top3の装飾ライン */}
          {medalStyle && (
            <Box
              position="absolute"
              left={0}
              top={0}
              bottom={0}
              w="4px"
              bg={medalStyle.bg}
            />
          )}
        </Box>
      </Link>
    </CLink>
  );
}
